import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';

interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: {
    name: string;
    avatar: string;
    role: string;
  };
  date: string;
  readTime: string;
  image: string;
  featured?: boolean;
  tags: string[];
}

const Blog: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'guides', label: 'Guides & Tips' },
    { id: 'industry', label: 'Industry Insights' },
  ];

  const blogPosts: BlogPost[] = [
    {
      id: 'ebay-dropshipping-uk-guide',
      slug: 'ebay-dropshipping-uk-guide',
      title: 'eBay dropshipping UK: the complete beginner\'s guide (2026)',
      excerpt: 'Everything you need to know about starting an eBay dropshipping business in the UK — what it is, how it works, legal considerations, and step-by-step setup.',
      category: 'guides',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'April 10, 2026',
      readTime: '12 min read',
      image: '/images/blog/blog-uk-guide-cover.webp',
      featured: true,
      tags: ['Dropshipping', 'eBay UK', 'Beginner Guide']
    },
    {
      id: 'amazon-to-ebay-arbitrage',
      slug: 'amazon-to-ebay-arbitrage',
      title: 'Amazon to eBay arbitrage: how to find profitable products',
      excerpt: 'Learn proven methods for finding products on Amazon that sell for more on eBay. Covers product research, profit calculation, and what to avoid.',
      category: 'guides',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'April 7, 2026',
      readTime: '10 min read',
      image: '/images/blog/blog-amazon-arbitrage-cover.webp',
      featured: true,
      tags: ['Arbitrage', 'Product Research', 'Amazon']
    },
    {
      id: 'best-ebay-dropshipping-software',
      slug: 'best-ebay-dropshipping-software',
      title: 'Best eBay dropshipping software compared (2026)',
      excerpt: 'An honest comparison of the top eBay dropshipping tools — features, pricing, and what to look for when choosing the right platform for your business.',
      category: 'industry',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'April 3, 2026',
      readTime: '9 min read',
      image: '/images/blog/blog-seller-dashboard.webp',
      tags: ['Software', 'Comparison', 'Tools']
    },
    {
      id: 'ebay-vero-dropshipping',
      slug: 'ebay-vero-dropshipping',
      title: 'How to avoid eBay VeRO violations when dropshipping',
      excerpt: 'VeRO takedowns can kill your eBay account. Learn what VeRO is, which brands to avoid, how to check before listing, and what to do if you get a claim.',
      category: 'guides',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'March 28, 2026',
      readTime: '8 min read',
      image: '/images/blog/blog-dropshipping-alert.webp',
      tags: ['VeRO', 'Compliance', 'Account Health']
    },
    {
      id: 'ebay-repricing-strategies',
      slug: 'ebay-repricing-strategies',
      title: 'eBay repricing strategies: how to stay competitive without racing to the bottom',
      excerpt: 'Pricing your eBay listings too low eats your margins. Too high and you lose sales. Here\'s how to find the sweet spot with smart repricing strategies.',
      category: 'guides',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'March 22, 2026',
      readTime: '8 min read',
      image: '/images/blog/blog-repricing-strategy.webp',
      tags: ['Repricing', 'Pricing', 'Strategy']
    },
    {
      id: 'ebay-dropshipping-mistakes',
      slug: 'ebay-dropshipping-mistakes',
      title: '10 most common eBay dropshipping mistakes (and how to fix them)',
      excerpt: 'From pricing errors to stock sync failures — the mistakes that cost new dropshippers money, and practical fixes for each one.',
      category: 'guides',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'March 15, 2026',
      readTime: '8 min read',
      image: '/images/blog/blog-dropshipping-alert.webp',
      tags: ['Mistakes', 'Tips', 'Beginner']
    },
    {
      id: 'ebay-title-optimisation',
      slug: 'ebay-title-optimisation',
      title: 'How to write eBay titles that rank: a seller\'s SEO guide',
      excerpt: 'Your eBay title is the single biggest factor in whether buyers find your listing. Learn the rules, character limits, and keyword strategies that work.',
      category: 'guides',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'March 8, 2026',
      readTime: '7 min read',
      image: '/images/blog/blog-title-seo.webp',
      tags: ['eBay SEO', 'Titles', 'Optimisation']
    },
    {
      id: 'is-ebay-dropshipping-profitable',
      slug: 'is-ebay-dropshipping-profitable',
      title: 'Is eBay dropshipping still profitable in 2026?',
      excerpt: 'An honest look at the numbers — realistic margins, startup costs, time investment, and whether eBay dropshipping is worth starting today.',
      category: 'industry',
      author: { name: 'Coderom Team', role: 'Editorial', avatar: 'C' },
      date: 'March 1, 2026',
      readTime: '7 min read',
      image: '/images/blog/blog-profitability-2026.webp',
      tags: ['Profitability', 'Analysis', 'Market Trends']
    },
  ];

  const filteredPosts = activeCategory === 'all'
    ? blogPosts
    : blogPosts.filter(post => post.category === activeCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);
  const regularPosts = filteredPosts.filter(post => !post.featured);

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="CodeRom Blog — eBay Dropshipping Tips"
        description="Guides, tutorials & news to grow your eBay dropshipping business with CodeRom."
      />
      <Navbar />
    <div className="blog-page">
      {/* Header */}
      <section className="blog-header">
        <div className="header-bg">
          <div className="header-pattern" />
        </div>
        <Container>
          <motion.div
            className="header-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
                        <div className="header-badge">
              <span className="badge-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19l7-7 3 3-7 7-3-3z" />
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
                  <path d="M2 2l7.586 7.586" />
                  <circle cx="11" cy="11" r="2" />
                </svg>
              </span>
              CodeRom Blog
            </div>
            <h1>Insights & Updates</h1>
            <p>Tips, tutorials, and news to help you grow your dropshipping business</p>
          </motion.div>
        </Container>
      </section>

      {/* Featured Posts */}
      {activeCategory === 'all' && (
        <section className="featured-section">
          <Container>
            <Row className="g-4">
              {featuredPosts.map((post, index) => (
                <Col lg={6} key={post.id}>
                  <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <motion.article
                      className="featured-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8 }}
                    >
                      <div className="featured-image">
                        <img
                          src={post.image}
                          alt={post.title}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                        <span className="featured-badge">Featured</span>
                      </div>
                      <div className="featured-content">
                        <div className="post-tags">
                          {post.tags.slice(0, 2).map((tag, idx) => (
                            <span key={idx} className="tag">{tag}</span>
                          ))}
                        </div>
                        <h2>{post.title}</h2>
                        <p>{post.excerpt}</p>
                        <div className="post-meta">
                          <div className="author">
                            <div className="author-avatar">
                              {post.author.name.charAt(0)}
                            </div>
                            <div className="author-info">
                              <span className="author-name">{post.author.name}</span>
                              <span className="author-role">{post.author.role}</span>
                            </div>
                          </div>
                          <div className="post-date">
                            <span>{post.date}</span>
                            <span className="dot">•</span>
                            <span>{post.readTime}</span>
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  </Link>
                </Col>
              ))}
            </Row>
          </Container>
        </section>
      )}

      {/* Category Filter */}
      <section className="filter-section">
        <Container>
          <div className="filter-wrapper">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`filter-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* Blog Posts Grid */}
      <section className="posts-section">
        <Container>
          <Row className="g-4">
            {regularPosts.map((post, index) => (
              <Col lg={4} md={6} key={post.id}>
                <Link to={`/blog/${post.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <motion.article
                    className="post-card"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ y: -5 }}
                  >
                    <div className="post-image">
                      <img
                        src={post.image}
                        alt={post.title}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <span className="category-badge">{categories.find(c => c.id === post.category)?.label}</span>
                    </div>
                    <div className="post-content">
                      <h3>{post.title}</h3>
                      <p>{post.excerpt}</p>
                      <div className="post-footer">
                        <div className="author-mini">
                          <div className="author-avatar-mini">
                            {post.author.name.charAt(0)}
                          </div>
                          <span>{post.author.name}</span>
                        </div>
                        <span className="read-time">{post.readTime}</span>
                      </div>
                    </div>
                  </motion.article>
                </Link>
              </Col>
            ))}
          </Row>

        </Container>
      </section>

      {/* Newsletter */}
      <section className="newsletter-section">
        <Container>
          <motion.div
            className="newsletter-wrapper"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="newsletter-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            </div>
            <h2>Subscribe to our newsletter</h2>
            <p>Get the latest tips, tutorials, and product updates delivered to your inbox weekly.</p>
            <form className="newsletter-form">
              <input type="email" placeholder="Enter your email" />
              <button type="submit">Subscribe</button>
            </form>
            <span className="newsletter-note">No spam, ever. Unsubscribe anytime.</span>
          </motion.div>
        </Container>
      </section>

      <style>{`
        .blog-page {
          min-height: 100vh;
          background: #f8fafc;
        }

        .blog-header {
          position: relative;
          padding: 100px 0 60px;
          overflow: hidden;
        }

        .header-bg {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.72) 0%, rgba(15, 23, 42, 0.55) 100%),
                      url('https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1920&q=80') center/cover no-repeat;
        }

        .header-pattern {
          position: absolute;
          inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }

        .header-content {
          position: relative;
          z-index: 1;
          text-align: center;
          color: white;
        }

        .back-link {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: rgba(255,255,255,0.7);
          text-decoration: none;
          font-size: 14px;
          margin-bottom: 24px;
          transition: color 0.2s;
        }

        .back-link:hover {
          color: white;
        }

        .header-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(249, 115, 22, 0.2);
          border: 1px solid rgba(249, 115, 22, 0.3);
          border-radius: 24px;
          font-size: 14px;
          font-weight: 600;
          color: #fb923c;
          margin-bottom: 20px;
        }

        .badge-icon {
          display: flex;
        }

        .blog-header h1 {
          font-size: 52px;
          font-weight: 800;
          margin-bottom: 16px;
        }

        .blog-header p {
          font-size: 20px;
          opacity: 0.7;
        }

        .featured-section {
          padding: 60px 0 0;
        }

        .featured-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          cursor: pointer;
          transition: all 0.3s;
          height: 100%;
        }

        .featured-card:hover {
          box-shadow: 0 20px 40px rgba(0,0,0,0.12);
        }

        .featured-image {
          position: relative;
          height: 240px;
          background: linear-gradient(135deg, #1e293b, #334155);
        }

        .image-placeholder {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.3);
        }

        .featured-badge {
          position: absolute;
          top: 16px;
          left: 16px;
          padding: 6px 14px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          font-size: 12px;
          font-weight: 700;
          border-radius: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .featured-content {
          padding: 28px;
        }

        .post-tags {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
        }

        .tag {
          padding: 4px 12px;
          background: #f1f5f9;
          color: #64748b;
          font-size: 12px;
          font-weight: 600;
          border-radius: 16px;
        }

        .featured-content h2 {
          font-size: 22px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
          line-height: 1.4;
        }

        .featured-content p {
          font-size: 15px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }

        .post-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }

        .author {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .author-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 16px;
        }

        .author-info {
          display: flex;
          flex-direction: column;
        }

        .author-name {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }

        .author-role {
          font-size: 12px;
          color: #64748b;
        }

        .post-date {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #94a3b8;
        }

        .dot {
          font-size: 8px;
        }

        .filter-section {
          padding: 40px 0;
          position: sticky;
          top: 0;
          background: #f8fafc;
          z-index: 100;
        }

        .filter-wrapper {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .filter-btn {
          padding: 10px 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 24px;
          font-size: 14px;
          font-weight: 500;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
        }

        .filter-btn:hover {
          border-color: #f97316;
          color: #f97316;
        }

        .filter-btn.active {
          background: linear-gradient(135deg, #f97316, #fb923c);
          border-color: transparent;
          color: white;
        }

        .posts-section {
          padding: 0 0 60px;
        }

        .post-card {
          background: white;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 2px 12px rgba(0,0,0,0.04);
          cursor: pointer;
          transition: all 0.3s;
          height: 100%;
          border: 1px solid #e2e8f0;
        }

        .post-card:hover {
          border-color: #f97316;
          box-shadow: 0 12px 32px rgba(0,0,0,0.08);
        }

        .post-image {
          position: relative;
          height: 180px;
          background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
        }

        .category-badge {
          position: absolute;
          top: 12px;
          left: 12px;
          padding: 4px 12px;
          background: white;
          color: #f97316;
          font-size: 11px;
          font-weight: 700;
          border-radius: 16px;
        }

        .post-content {
          padding: 20px;
        }

        .post-content h3 {
          font-size: 17px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 10px;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .post-content p {
          font-size: 14px;
          color: #64748b;
          line-height: 1.5;
          margin-bottom: 16px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .post-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .author-mini {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .author-avatar-mini {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 12px;
        }

        .author-mini span {
          font-size: 13px;
          color: #64748b;
        }

        .read-time {
          font-size: 12px;
          color: #94a3b8;
        }

        .load-more {
          text-align: center;
          margin-top: 48px;
        }

        .load-more-btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 32px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s;
        }

        .load-more-btn:hover {
          border-color: #f97316;
          color: #f97316;
        }

        .newsletter-section {
          padding: 80px 0;
          background: white;
        }

        .newsletter-wrapper {
          text-align: center;
          max-width: 500px;
          margin: 0 auto;
        }

        .newsletter-icon {
          width: 80px;
          height: 80px;
          margin: 0 auto 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          border-radius: 20px;
          color: #f97316;
        }

        .newsletter-wrapper h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
        }

        .newsletter-wrapper > p {
          color: #64748b;
          font-size: 16px;
          margin-bottom: 32px;
        }

        .newsletter-form {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
        }

        .newsletter-form input {
          flex: 1;
          padding: 14px 20px;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          font-size: 15px;
          outline: none;
          transition: border-color 0.2s;
        }

        .newsletter-form input:focus {
          border-color: #f97316;
        }

        .newsletter-form button {
          padding: 14px 28px;
          background: linear-gradient(135deg, #f97316, #fb923c);
          color: white;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .newsletter-form button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(249, 115, 22, 0.35);
        }

        .newsletter-note {
          font-size: 13px;
          color: #94a3b8;
        }

        @media (max-width: 768px) {
          .blog-header h1 {
            font-size: 32px;
          }

          .featured-image {
            height: 180px;
          }

          .featured-content h2 {
            font-size: 18px;
          }

          .newsletter-form {
            flex-direction: column;
          }

          .newsletter-form button {
            width: 100%;
          }
        }
      `}</style>
    </div>
    <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default Blog;
